// SystemService.js
import { BaseService } from './BaseService';
import { createLogger } from './Debug';
export class SystemService extends BaseService {
  constructor() {
    super();
  }

  /**
   * Get system message for the current platform
   * @param {string} platform - 'web' | 'electron' | 'ios' | 'android' | 'all'
   * @returns {Promise<Object|null>} System message data or null
   */
  async getSystemMessage(platform = 'all') {
    try {
      const response = await this.get(`/system/message?platform=${platform}`);
      return response.data || null;
    } catch (error) {
      const log = createLogger('SystemService.getSystemMessage'); 
      log.error('Error fetching system message:', error);
      return null;
    }
  }

  /**
   * Check for app updates
   * @param {string} platform - 'electron' | 'ios' | 'android'
   * @param {string} version - Current app version
   * @param {number} build - Current build number (optional)
   * @returns {Promise<Object>} Version check response
   */
  async checkVersion(platform, version, build) {
    try {
      const params = new URLSearchParams({ platform, version });
      if (build !== undefined && build !== null) {
        params.append('build', String(build));
      }
      
      const response = await this.get(`/system/version?${params.toString()}`);
      return response.data || { latest: null, needsUpdate: false };
    } catch (error) {
      const log = createLogger('SystemService.checkVersion');
      log.error('Error checking version:', error);
      return { latest: null, needsUpdate: false };
    }
  }

  /**
   * Check if a message should be shown (not seen before or updated)
   * @param {Object} message - System message object
   * @returns {boolean} Whether the message should be shown
   */
  shouldShowMessage(message) {
    if (!message) return false;
    
    const key = `systemMessageSeenAt:${message.id}`;
    const lastSeen = localStorage.getItem(key);
    return !lastSeen || lastSeen !== message.updatedAt;
  }

  /**
   * Mark a message as seen
   * @param {Object} message - System message object
   */
  markMessageSeen(message) {
    if (!message) return;
    
    const key = `systemMessageSeenAt:${message.id}`;
    localStorage.setItem(key, message.updatedAt);
  }

  /**
   * Detect the current platform
   * @returns {string} Platform identifier
   */
  static detectPlatform() {
    // Check for Electron
    if (window.isElectron || window.electronAPI) {
      return 'electron';
    }
    
    // Check for Capacitor/Mobile
    try {
      const { Capacitor } = require('@capacitor/core');
      if (Capacitor.isNativePlatform()) {
        const platform = Capacitor.getPlatform();
        if (platform === 'ios') return 'ios';
        if (platform === 'android') return 'android';
      }
    } catch (e) {
      // Capacitor not available
    }
    
    // Check user agent for mobile web
    const userAgent = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(userAgent)) {
      return 'ios';
    }
    if (/android/.test(userAgent)) {
      return 'android';
    }
    
    return 'web';
  }
}